/**
 * Customer reliability (backend customer-reliability.ts): how often a mobile
 * no-showed, cancelled late, or kept moving bookings. Drives the ⚠ on the
 * Calendar and the warning when staff book someone in.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

export interface CustomerReliability {
  visits: number;
  noShows: number;
  lateCancels: number;
  moves: number;
  flag: 'misses' | 'keeps-moving' | null;
}

/** Same rule as the backend's normalizeMobile: an Australian mobile. */
export function isAuMobile(raw: string | null | undefined): boolean {
  const cleaned = String(raw || '').replace(/[\s\-()]/g, '');
  return /^(\+?61|0)?4\d{8}$/.test(cleaned);
}

/** The backend keys reliability by the last 9 digits of the mobile. */
export function phoneKey(raw: string | null | undefined): string | null {
  const key = String(raw || '')
    .replace(/\D/g, '')
    .slice(-9);
  return /^4\d{8}$/.test(key) ? key : null;
}

/**
 * Looks up every mobile in one request and returns a getter that gives the
 * record only for customers worth a warning (flag set).
 */
export function useCustomerReliability(
  phones: (string | null | undefined)[],
): (phone: string | null | undefined) => CustomerReliability | null {
  const keys = [...new Set(phones.map(phoneKey).filter((k): k is string => Boolean(k)))].sort();
  const { data } = useQuery({
    queryKey: ['customer-reliability', keys],
    queryFn: () => api.getCustomerReliability(keys),
    enabled: keys.length > 0,
    staleTime: 5 * 60_000,
  });
  return (phone) => {
    const key = phoneKey(phone);
    const r = key ? data?.[key] : undefined;
    return r?.flag ? r : null;
  };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** One line for staff, e.g. "2 no-shows · 1 late cancel · 3 cuts". */
export function reliabilityText(r: CustomerReliability): string {
  const cuts = r.visits === 0 ? 'never come in' : plural(r.visits, 'cut', 'cuts');
  if (r.flag === 'keeps-moving') {
    return `Moved ${plural(r.moves, 'booking', 'bookings')} · ${cuts}`;
  }
  const parts = [];
  if (r.noShows) parts.push(plural(r.noShows, 'no-show', 'no-shows'));
  if (r.lateCancels) parts.push(plural(r.lateCancels, 'late cancel', 'late cancels'));
  parts.push(cuts);
  return parts.join(' · ');
}
